import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { skip } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { MenuService } from '../../core/services/menu.service';
import { MenuItem } from '../../core/layout/models/menu-item.model';
import { Role } from '../../core/enums/role.enum';
import { CadastroServicoService } from '../servico-radioterapia/services/cadastro-servico.service';
import { ServicoRadioterapiaCnesContextService } from '../servico-radioterapia/services/servico-radioterapia-cnes-context.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private menuService = inject(MenuService);
  private router = inject(Router);
  private cadastroServico = inject(CadastroServicoService);
  private servicoRadioterapiaCtx = inject(ServicoRadioterapiaCnesContextService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private menuSubscription: Subscription | null = null;

  readonly featuresEquipamentos = environment.features.equipamentos;

  nomeUsuario = '';
  primeiroNome = '';
  menuItems: MenuItem[] = [];
  allMenuItems: MenuItem[] = [];
  isServicoRadioterapiaPreencher = false;

  /** Enquanto consulta GET cadastro/status (multi ou token CNES). */
  onboardingCtaLoading = false;
  /** Exibe CTA “Iniciar Onboarding” (cadastro ainda não enviado para o contexto). */
  showOnboardingPendente = false;
  /** Cadastro enviado e pendente de validação federal. */
  showOnboardingEmAnalise = false;
  /** Cadastro reprovado pelo MS (banner vermelho com justificativa). */
  showOnboardingReprovado = false;
  justificativaRejeicao: string | null = null;

  ngOnInit(): void {
    this.carregarDadosUsuario();
    this.carregarMenus();

    this.servicoRadioterapiaCtx.cnesSessao$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isServicoRadioterapiaPreencher) {
          this.carregarStatusOnboardingBanner();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.menuSubscription) {
      this.menuSubscription.unsubscribe();
      this.menuSubscription = null;
    }
  }

  private carregarDadosUsuario(): void {
    const userInfo = this.authService.getUserInfo();
    if (userInfo) {
      this.nomeUsuario = userInfo.nome;
      this.primeiroNome = userInfo.nome.split(' ')[0];
    }
    this.isServicoRadioterapiaPreencher = this.authService.hasRole(Role.CADASTRO_SERVICO_PREENCHER);
    if (this.isServicoRadioterapiaPreencher) {
      this.carregarStatusOnboardingBanner();
    }
  }

  private carregarStatusOnboardingBanner(): void {
    this.onboardingCtaLoading = true;
    this.showOnboardingPendente = false;
    this.showOnboardingEmAnalise = false;
    this.showOnboardingReprovado = false;
    this.justificativaRejeicao = null;

    const cnesContexto = this.servicoRadioterapiaCtx.getCnesEfetivo();

    if (!cnesContexto) {
      this.onboardingCtaLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.cadastroServico
      .obterStatus()
      .pipe(
        finalize(() => {
          this.onboardingCtaLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (s) => {
          this.aplicarBannerOnboarding(s);
          this.cdr.markForCheck();
        },
        error: () => {
          this.showOnboardingPendente = false;
          this.showOnboardingEmAnalise = false;
          this.showOnboardingReprovado = false;
          this.justificativaRejeicao = null;
          this.cdr.markForCheck();
        },
      });
  }

  private aplicarBannerOnboarding(s: {
    cadastroEnviado: boolean;
    status: string | null;
    justificativaRejeicao?: string | null;
  }): void {
    if (!s.cadastroEnviado) {
      this.showOnboardingPendente = true;
      this.showOnboardingEmAnalise = false;
      this.showOnboardingReprovado = false;
      this.justificativaRejeicao = null;
      return;
    }

    const status = (s.status ?? '').trim().toLowerCase();
    if (status === 'validado') {
      this.showOnboardingPendente = false;
      this.showOnboardingEmAnalise = false;
      this.showOnboardingReprovado = false;
      this.justificativaRejeicao = null;
      return;
    }

    if (status === 'rejeitado') {
      this.showOnboardingPendente = false;
      this.showOnboardingEmAnalise = false;
      this.showOnboardingReprovado = true;
      this.justificativaRejeicao = s.justificativaRejeicao?.trim() || null;
      return;
    }

    this.showOnboardingPendente = false;
    this.showOnboardingEmAnalise = true;
    this.showOnboardingReprovado = false;
    this.justificativaRejeicao = null;
  }

  private carregarMenus(): void {
    this.menuSubscription = this.menuService.menuSubject.subscribe({
      next: (menu: MenuItem[]) => {
        this.menuItems = menu;
        this.allMenuItems = this.flattenMenuItems(menu);
      },
    });

    if (this.menuItems.length === 0 && this.authService.isAuthenticated()) {
      const roles = this.authService.getRoles();
      const built = this.menuService.getDefaultMenu(roles);
      this.menuItems = built;
      this.allMenuItems = this.flattenMenuItems(built);
      this.menuService.updateMenu(built);
    }
  }

  private flattenMenuItems(items: MenuItem[]): MenuItem[] {
    const result: MenuItem[] = [];

    const flatten = (menuItems: MenuItem[]) => {
      for (const item of menuItems) {
        if (item.route || item.url) {
          result.push(item);
        }
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      }
    };

    flatten(items);
    return result;
  }

  navegarPara(item: MenuItem): void {
    if (item.route) {
      this.router.navigate([item.route]);
    } else if (item.url) {
      window.open(item.url, '_blank', 'noopener');
    }
  }

  logout(): void {
    this.authService.logout();
  }
}
